import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, MessageCircle, HelpCircle, ArrowRight, LifeBuoy } from 'lucide-react';
import type { FAQItem } from '../SEO/schemas';
import { HELP_CENTER_URL } from '../../config/helpCenter';
import { MarketingFaqAnswer } from '../../utils/marketingFaqAnswerLinks';

interface FAQSectionProps {
  items: FAQItem[];
  title?: string;
  subtitle?: string;
}

export default function FAQSection({
  items,
  subtitle,
}: FAQSectionProps) {
  const { t } = useTranslation('marketing');
  const resolvedSubtitle = subtitle ?? t('faqSection.defaultSubtitle');
  const [openIndex, setOpenIndex] = useState<number | null>(0); // Open first by default for better visual entrance

  return (
    <div className="lp-faq" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
      <div
        className="lp-faq__grid"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '80px', alignItems: 'start' }}
      >
        
        {/* Left Column: Title & Support CTA */}
        <div className="lp-faq__intro" style={{ position: 'sticky', top: '120px' }}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2
              style={{
                fontFamily: 'Plus Jakarta Sans',
                fontSize: 'clamp(40px, 5vw, 56px)',
                fontWeight: 900,
                color: '#1e293b',
                letterSpacing: '-0.05em',
                marginBottom: '24px',
                lineHeight: 1.1
              }}
            >
              {t('faqSection.headingLine1')} <br />
              <span style={{ fontFamily: 'Plus Jakarta Sans', fontStyle: 'normal', fontWeight: 700, color: '#A5765C' }}>
                {t('faqSection.headingLine2')}
              </span>
            </h2>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '18px',
                color: '#64748b',
                lineHeight: 1.6,
                marginBottom: '48px'
              }}
            >
              {resolvedSubtitle}
            </p>

            {/* Support Center Card */}
            <motion.a
              href={HELP_CENTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5, scale: 1.02 }}
              style={{
                display: 'block',
                background: 'white',
                padding: '32px',
                borderRadius: '32px',
                border: '1px solid rgba(165, 118, 92, 0.15)',
                textDecoration: 'none',
                boxShadow: '0 20px 40px rgba(165,118,92,0.05)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', background: 'rgba(165,118,92,0.03)', borderRadius: '50%' }} />
              
              <div style={{ 
                width: '48px', 
                height: '48px', 
                background: '#A5765C', 
                borderRadius: '16px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                marginBottom: '20px',
                color: 'white',
                boxShadow: '0 8px 16px rgba(165,118,92,0.3)'
              }}>
                <LifeBuoy size={24} />
              </div>
              
              <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 900, color: '#1e293b' }}>{t('faqSection.helpCenter.title')}</h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '15px', color: '#64748b', lineHeight: 1.5 }}>
                {t('faqSection.helpCenter.description')}
              </p>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#A5765C', fontWeight: 800, fontSize: '14px' }}>
                {t('faqSection.helpCenter.link')} <ArrowRight size={16} />
              </div>
            </motion.a>
          </motion.div>
        </div>

        {/* Right Column: Accordion */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {items.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                style={{
                  background: isOpen ? 'white' : 'transparent',
                  borderRadius: '32px',
                  border: isOpen ? '1px solid rgba(165,118,92,0.15)' : '1px solid transparent',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: isOpen ? '0 30px 60px rgba(0,0,0,0.04)' : 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '32px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    gap: '24px',
                    borderRadius: '32px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isOpen) {
                      e.currentTarget.parentElement!.style.background = 'rgba(165,118,92,0.03)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isOpen) {
                      e.currentTarget.parentElement!.style.background = 'transparent';
                    }
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Plus Jakarta Sans',
                      fontSize: '19px',
                      fontWeight: 800,
                      color: '#1e293b',
                      flex: 1,
                      transition: 'color 0.3s ease'
                    }}
                  >
                    {item.question}
                  </span>
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '14px', 
                      background: isOpen ? '#1e293b' : 'white',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      color: isOpen ? 'white' : '#1e293b'
                    }}
                  >
                    <ChevronDown size={20} />
                  </motion.div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div
                        style={{
                          padding: '0 32px 40px',
                          fontFamily: 'Inter',
                          fontSize: '17px',
                          lineHeight: 1.7,
                          color: '#64748b',
                          fontWeight: 500
                        }}
                      >
                        <MarketingFaqAnswer answer={item.answer} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {/* Bottom Callout */}
          <div
            className="lp-faq__more"
            style={{ 
            marginTop: '32px', 
            padding: '40px', 
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', 
            borderRadius: '40px',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '32px'
          }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 900 }}>{t('faqSection.moreQuestions.title')}</h3>
              <p style={{ margin: 0, opacity: 0.8, fontSize: '15px' }}>{t('faqSection.moreQuestions.description')}</p>
            </div>
            <motion.a
              href="mailto:contact@rintintin-booking.com"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                background: 'white',
                color: '#1e293b',
                padding: '16px 32px',
                borderRadius: '100px',
                fontWeight: 800,
                fontSize: '15px',
                textDecoration: 'none',
                boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
              }}
            >
              {t('faqSection.moreQuestions.cta')}
            </motion.a>
          </div>
        </div>
      </div>
    </div>
  );
}
